// Builds the live broadcast ticker from real-time data (NWS alerts, SPC outlook, current obs, forecast, gameday)
// plus admin headlines. Every item carries a category tag; urgent items turn the bar red.
import { GA_COUNTIES } from "./gaCounties";

const ET = "America/New_York";
const URGENT_RE = /tornado warning|severe thunderstorm warning|flash flood warning|extreme wind|hurricane warning|emergency/i;
const RISK = { TSTM: "General thunderstorms", MRGL: "Marginal risk of severe storms", SLGT: "Slight risk of severe storms", ENH: "Enhanced risk of severe storms", MDT: "Moderate risk of severe storms", HIGH: "HIGH risk of severe storms" };

const timeET = (d) => new Date(d).toLocaleTimeString("en-US", { timeZone: ET, hour: "numeric", minute: "2-digit" });
const dayET = (d, now) => {
  const dt = new Date(d);
  const sameDay = dt.toLocaleDateString("en-US", { timeZone: ET }) === now.toLocaleDateString("en-US", { timeZone: ET });
  const tomorrow = new Date(now.getTime() + 86400000).toLocaleDateString("en-US", { timeZone: ET }) === dt.toLocaleDateString("en-US", { timeZone: ET });
  return sameDay ? "Today" : tomorrow ? "Tomorrow" : dt.toLocaleDateString("en-US", { timeZone: ET, weekday: "long" });
};
const ZONE_PREFIX = /^(coastal|inland|north|south)\s+/i;
const isGeorgia = (part) => /,\s*GA\b/i.test(part) || (!/,\s*[A-Z]{2}\b/.test(part) && GA_COUNTIES.has(part.replace(ZONE_PREFIX, "").trim()));
const counties = (area = "") => {
  const parts = area.split(";").map((s) => s.trim()).filter(Boolean);
  const names = parts.filter(isGeorgia).map((s) => s.replace(/,\s*GA\b.*$/i, "").trim());
  if (!names.length) return "";
  if (names.length <= 3) return names.join(", ") + (names.length === 1 ? " County" : " counties");
  return `${names.slice(0, 3).join(", ")} and ${names.length - 3} more counties`;
};
const alertLine = (a) => {
  const c = counties(a.area);
  return c ? `${a.title} · ${c}${a.expires ? ` · until ${timeET(a.expires)}` : ""}` : "";
};

// --- Confirmed ground-truth Local Storm Reports (spotter-verified) ---
// Only significant reports earn the red "Confirmed" crawl: any tornado/funnel,
// hail ≥ 1" (quarter size+), and damaging wind / gusts ≥ 58 mph (severe criteria).
const magVal = (mag) => {
  if (!mag) return null;
  const m = parseFloat(mag);
  return Number.isNaN(m) ? null : m;
};
const confirmedLine = (r) => {
  const where = r.city || (r.county ? `${r.county} County` : "Georgia");
  const co = r.city && r.county ? ` · ${r.county} Co.` : "";
  const t = r.valid ? ` · ${timeET(r.valid)}` : "";
  if (r.cat === "tornado") {
    const kind = /funnel/i.test(r.label || "") ? "Funnel cloud" : "Tornado";
    return `${kind} reported near ${where}${co}${t}`;
  }
  if (r.cat === "hail") return `Large hail${r.mag ? ` ${r.mag}` : ""} near ${where}${co}${t}`;
  if (r.cat === "wind") {
    const label = /damage|tree/i.test(r.label || "") ? "Wind damage" : `Damaging wind${r.mag ? ` ${r.mag}` : ""}`;
    return `${label} near ${where}${co}${t}`;
  }
  return "";
};
const confirmedReports = (reports = []) => {
  const out = [];
  for (const r of reports) {
    let keep = false;
    if (r.cat === "tornado") keep = true;
    else if (r.cat === "hail") keep = (magVal(r.mag) ?? 0) >= 1;
    else if (r.cat === "wind") {
      const v = magVal(r.mag);
      const mph = /kt/i.test(r.mag || "") && v != null ? v * 1.15 : v;
      keep = /damage|tree/i.test(r.label || "") || (mph != null && mph >= 58);
    }
    if (keep) out.push(r);
    if (out.length >= 5) break;
  }
  return out;
};

export function buildTickerFeed({ alerts = [], cities = [], spcDays = [], tropical = [], games = [], adminItems = [], stormReports = null, now = new Date() }) {
  const items = [];
  const push = (tag, text, urgent = false) => text && items.push({ tag, text, urgent });

  // 0. Confirmed spotter reports (ground-truth) — highest-priority red crawl
  if (stormReports?.active) {
    confirmedReports(stormReports.reports || []).forEach((r) => push("confirmed", confirmedLine(r), true));
  }

  // 1. Active NWS alerts (expired ones never air)
  const live = alerts.filter((a) => !a.expires || new Date(a.expires) > now);
  const warn = live.filter((a) => URGENT_RE.test(a.title || ""));
  const other = live.filter((a) => !URGENT_RE.test(a.title || ""));
  warn.slice(0, 4).forEach((a) => push("warning", alertLine(a), true));

  // 1b. Tropical systems (NHC) — Atlantic/Gulf
  tropical.slice(0, 3).forEach((s) => {
    const where = s.dist_mi != null ? ` · ${s.dist_mi} mi ${s.bearing} of Atlanta` : "";
    const sev = s.classification === "HU" || s.classification === "MH";
    push("tropical", `${s.short} ${s.name} · ${s.wind_mph} mph · moving ${s.movement}${where}`, sev && (s.dist_mi == null || s.dist_mi < 700));
  });

  // 2. Admin breaking headlines
  adminItems.filter((i) => i.priority === "urgent").forEach((i) => push("breaking", i.text, true));

  // 3. SPC outlook for Georgia (today only; TSTM is routine)
  const day1 = spcDays.find((d) => d.day === 1 && d.ga_risk);
  if (day1) push("outlook", day1.ga_risk === "TSTM" ? "SPC outlook: general thunderstorms possible across Georgia today" : `SPC Day 1 outlook: ${RISK[day1.ga_risk] || day1.ga_risk} across parts of Georgia today`);

  // 4. Watches / advisories / statements
  other.slice(0, 4).forEach((a) => push("alert", alertLine(a)));

  // 5. Current conditions (two groups of cities)
  const obs = cities.filter((c) => c.current_temp != null);
  const fmt = (c) => `${c.name} ${Math.round(c.current_temp)}°${c.condition ? ` ${c.condition}` : ""}`;
  if (obs.length) {
    const half = Math.ceil(obs.length / 2);
    push("now", obs.slice(0, half).map(fmt).join(" · "));
    if (obs.length > half) push("now", obs.slice(half).map(fmt).join(" · "));
    const temps = obs.map((c) => Math.round(c.current_temp));
    const hot = obs[temps.indexOf(Math.max(...temps))], cold = obs[temps.indexOf(Math.min(...temps))];
    if (hot && cold && hot !== cold) push("now", `Warmest: ${hot.name} ${Math.round(hot.current_temp)}° · Coolest: ${cold.name} ${Math.round(cold.current_temp)}°`);
  }

  // 6. Forecast — Atlanta next two periods + statewide high range tomorrow
  const atl = cities.find((c) => c.id === "atlanta") || cities[0];
  (atl?.forecast_periods || []).slice(0, 2).forEach((p) => push("forecast", `Atlanta ${p.name}: ${p.shortForecast}, ${p.isDaytime ? "high" : "low"} ${p.temperature}°`));
  const nextDay = (c) => (c.forecast_periods || []).find((p) => p.isDaytime);
  const dayHighs = cities.map(nextDay).filter((p) => p && typeof p.temperature === "number");
  const atlDay = atl && nextDay(atl);
  if (dayHighs.length >= 3 && atlDay) push("forecast", `Highs across Georgia ${atlDay.name}: ${Math.min(...dayHighs.map((p) => p.temperature))}–${Math.max(...dayHighs.map((p) => p.temperature))}°`);

  // 7. Gameday within 72 h
  games.filter((g) => g?.date && new Date(g.date) - now < 72 * 3600000 && new Date(g.date) - now > -4 * 3600000).forEach((g) => {
    const when = `${dayET(g.date, now)} ${timeET(g.date)}`;
    const wx = g.indoor ? "indoors" : g.weather?.shortForecast ? `${g.weather.shortForecast}${g.weather.temperature != null ? `, ${g.weather.temperature}°` : ""}` : "";
    push("sports", `${g.short_name || g.name} · ${when} · ${g.venue || ""}${wx ? ` · ${wx}` : ""}`);
  });

  // 8. Admin routine headlines
  adminItems.filter((i) => i.priority !== "urgent").forEach((i) => push("news", i.text));

  return items;
}
